import { makeStyles } from '@material-ui/styles';
import {
    Theme,
    MainButton,
    UserSocialLink,
    TwitterIcon,
    LinkedInIcon,
} from '@bizzabo/design-system';
import cn from 'clsx';
import { UserPreview } from '@bizzabo/entities';
import Info from './Info';
import useTypeTracking from '../../../../../analytics';
import { useEffect, useState } from 'react';

interface ViewInfoProps {
    userByProfileId: UserPreview;
    isViewInfoOpen: boolean;
    toogleViewInfoHandle: () => void;
}

const ViewInfo: React.FC<ViewInfoProps> = ({
    userByProfileId,
    isViewInfoOpen,
    toogleViewInfoHandle,
}) => {
    const classes = useStyles();
    const { trackClickEvent } = useTypeTracking();
    const [isUserInfoHasData, setIsUserInfoHasData] = useState(false);

    const userInfo: Partial<UserPreview> = {
        company: userByProfileId?.company,
        country: userByProfileId?.country,
        state: userByProfileId?.state,
        summary: userByProfileId?.summary,
        linkedinId: userByProfileId?.linkedinId,
        twitterHandle: userByProfileId?.twitterHandle,
        web: userByProfileId?.web,
    };

    useEffect(() => {
        const userInfoHasData = Object.values(userInfo).some(value => value);
        setIsUserInfoHasData(userInfoHasData);
    }, [userInfo]);

    if (!isUserInfoHasData) {
        return null;
    }

    const sendEvent = (subCategory: string) => {
        trackClickEvent({
            subCategory,
        });
    };

    const trackViewInfo = () => {
        const detailsProvidedList = [];
        for (const [key, value] of Object.entries(userInfo)) {
            if (value === '' || value === undefined || value === null) {
                continue;
            }
            detailsProvidedList.push(key);
        }

        trackClickEvent({
            subCategory: 'PROFILE_EXPANSION',
            description: {
                details_provided: detailsProvidedList,
                match_type: null,
            },
        });
    };

    const twitterClick = () => {
        sendEvent('CLICKED_TWITTER_PROFILE_EXPANDED');
    };

    const linkedinClick = () => {
        sendEvent('CLICKED_LINKEDIN_PROFILE_EXPANDED');
    };

    const websiteClick = () => {
        sendEvent('CLICKED_WEBSITE_PROFILE_EXPANDED');
    };

    const toogleViewInfo = () => {
        if (!isViewInfoOpen) {
            trackViewInfo();
        }
        toogleViewInfoHandle();
    };

    return (
        <div className={cn(classes.viewInfo, { open: isViewInfoOpen })}>
            <MainButton
                onClick={toogleViewInfo}
                className={classes.btn}
                after={<div className={classes.triangle} />}
            >
                {isViewInfoOpen ? 'Hide info' : 'View info'}
            </MainButton>
            <div className={classes.infoCard}>
                <div className={classes.content}>
                    <div className={classes.group}>
                        {userInfo.company && (
                            <Info
                                title="Company"
                                text={userInfo.company}
                                className={classes.infoInGroup}
                            />
                        )}
                        {userInfo.web && (
                            <Info
                                className={classes.infoInGroup}
                                title="Company website"
                                text={userInfo.web}
                                isLink
                                onLinkClick={websiteClick}
                            />
                        )}
                        {userInfo.country && (
                            <Info
                                title="Country"
                                text={userInfo.country}
                                className={classes.infoInGroup}
                            />
                        )}
                        {userInfo.state && (
                            <Info
                                title="State"
                                text={userInfo.state}
                                className={classes.infoInGroup}
                            />
                        )}
                    </div>
                    {userInfo.summary && (
                        <Info
                            className={classes.longInfo}
                            textClassName={classes.longInfoText}
                            title="About me"
                            text={userInfo.summary}
                        />
                    )}
                    {(userInfo.linkedinId || userInfo.twitterHandle) && (
                        <div className={classes.socialLinks}>
                            {userInfo.linkedinId && (
                                <UserSocialLink
                                    referTo={userInfo.linkedinId}
                                    onLinkClick={linkedinClick}
                                >
                                    <LinkedInIcon />
                                </UserSocialLink>
                            )}
                            {userInfo.twitterHandle && (
                                <UserSocialLink
                                    referTo={`https://twitter.com/${userInfo.twitterHandle}`}
                                    onLinkClick={twitterClick}
                                >
                                    <TwitterIcon />
                                </UserSocialLink>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 },
        },
        viewInfo: {
            position: 'relative',
            transition: 'all 0.2s linear',
            background: theme.palette.greys[100],
            '&.open': {
                '& $infoCard': {
                    maxHeight: 340,
                    visibility: 'visible',
                    padding: '16px 24px',
                    backgroundColor: theme.palette.greys[200],
                    borderRadius: 16,
                    margin: '15px 0 24px',
                },
                '& $content': {
                    maxHeight: 292,
                    visibility: 'visible',
                },
                '& $triangle': {
                    transform: 'rotate(180deg)',
                },
            },
            width: '100%',
        },
        content: {
            maxHeight: 0,
            visibility: 'hidden',
            overflow: 'hidden',
        },
        infoCard: {
            marginTop: 16,
            maxHeight: 0,
            visibility: 'hidden',
            transition: 'visibility 0s, opacity 0.5s linear',
        },

        group: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        infoInGroup: {
            width: '50%',
        },
        longInfo: {
            maxHeight: 147,
        },
        longInfoText: {
            maxHeight: 129,
            overflowY: 'auto',
        },
        summary: {
            width: '100%',
        },
        btn: {
            ...theme.typography.t81,
            border: 'none',
            marginLeft: 33,
            marginTop: -10,
            color: 'black',
            fontSize: '12px',
            '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
            },
        },
        triangle: {
            width: 0,
            height: 0,
            transition: 'all 0.2s linear',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `4px solid black`,
            color: 'black',
        },
        socialLinks: {
            marginTop: 8,
            marginBottom: 8,
        },
    }),
    { classNamePrefix: ViewInfo.name }
);

export default ViewInfo;
